/**
 * Adukoo Helper — rule-based knowledge base + multi-language.
 *
 * v79.104: Comprehensive coverage. 40+ intents. In-house answers for
 * English + Hindi + Telugu + Tamil + Spanish. For every other language,
 * the client falls back to English answers (users can still ask in their
 * own language — matching also runs against localised triggers).
 *
 * Each intent has:
 *   - id, category, question
 *   - triggers: EN keywords the user might type
 *   - triggers_i18n: { hi: [...], te: [...], ta: [...], es: [...] }
 *   - answer_i18n: { en: "...", hi: "...", te: "...", ta: "...", es: "..." }
 *   - action: optional { label_i18n: {en/hi/te/ta/es}, to: "/route" }
 */

const RAW_INTENTS = [
    // ---------- Post an ad ----------
    {
        id: "post-ad",
        category: "Post",
        question: "How do I post an ad?",
        triggers: ["post ad", "post an ad", "sell", "list", "create listing", "add listing", "post my product"],
        triggers_i18n: {
            hi: ["विज्ञापन पोस्ट", "पोस्ट करना", "बेचना", "बिक्री"],
            te: ["ప్రకటన", "పోస్ట్", "అమ్మకం"],
            ta: ["விளம்பரம்", "பதிவு", "விற்பனை"],
            es: ["publicar anuncio", "poner en venta", "vender"],
        },
        answer_i18n: {
            en: "Posting is free and takes 60 seconds. Tap the yellow + Post button in the header, add a Title + one Photo + Category + City + Price, and you're live.",
            hi: "पोस्ट करना मुफ़्त है और 60 सेकंड लगते हैं। हेडर में पीले + पोस्ट बटन पर टैप करें, शीर्षक + एक फ़ोटो + श्रेणी + शहर + कीमत जोड़ें, बस।",
            te: "పోస్టింగ్ ఉచితం, 60 సెకన్లు పడతాయి. హెడర్‌లోని + Post బటన్ నొక్కి, శీర్షిక + ఫోటో + వర్గం + నగరం + ధర జోడించండి.",
            ta: "பதிவிடுவது இலவசம், 60 வினாடிகள் ஆகும். ஹெடரில் உள்ள + Post பொத்தானை தட்டவும், தலைப்பு + புகைப்படம் + வகை + நகரம் + விலை சேர்க்கவும்.",
            es: "Publicar es gratis y toma 60 segundos. Toca el botón amarillo + Post, añade título, una foto, categoría, ciudad y precio.",
        },
        action: {
            to: "/post-ad",
            label_i18n: { en: "Post an ad now", hi: "अभी विज्ञापन पोस्ट करें", te: "ఇప్పుడు పోస్ట్ చేయండి", ta: "இப்போது பதிவிடுங்கள்", es: "Publicar ahora" },
        },
    },
    {
        id: "boost-ad", category: "Post", question: "How do I boost my ad?",
        triggers: ["boost", "promote", "top of list", "featured", "highlight my ad"],
        triggers_i18n: { hi: ["बूस्ट", "प्रमोट"], te: ["బూస్ట్"], ta: ["பூஸ்ட்"], es: ["destacar", "impulsar"] },
        answer_i18n: {
            en: "Open your listing → tap Boost. Boosted ads appear at the top of their category + get a highlighted background for 7 days by default.",
            hi: "अपनी लिस्टिंग खोलें → Boost पर टैप करें। बूस्ट किए गए विज्ञापन 7 दिनों तक श्रेणी में सबसे ऊपर दिखते हैं।",
            te: "మీ లిస్టింగ్ తెరిచి → Boost నొక్కండి. బూస్ట్ చేసిన ప్రకటనలు 7 రోజులు వర్గంలో పైన కనిపిస్తాయి.",
            ta: "உங்கள் பதிவைத் திறந்து → Boost அழுத்தவும். பூஸ்ட் செய்த விளம்பரங்கள் 7 நாட்கள் வகையின் மேல் தோன்றும்.",
            es: "Abre tu anuncio → toca Boost. Los anuncios impulsados aparecen arriba de su categoría durante 7 días.",
        },
        action: { to: "/account/my-ads", label_i18n: { en: "My ads", hi: "मेरे विज्ञापन", te: "నా ప్రకటనలు", ta: "என் விளம்பரங்கள்", es: "Mis anuncios" } },
    },
    {
        id: "edit-delete", category: "Post", question: "How do I edit or delete an ad?",
        triggers: ["edit ad", "delete ad", "remove listing", "change price", "update ad"],
        triggers_i18n: { hi: ["एडिट", "डिलीट", "हटाना"], te: ["ఎడిట్", "తొలగించు"], ta: ["திருத்து", "நீக்கு"], es: ["editar", "eliminar"] },
        answer_i18n: {
            en: "Go to Account → My Ads. Tap the ⋯ menu on any ad to Edit, Boost, Clone or Delete.",
            hi: "Account → My Ads पर जाएं। किसी भी विज्ञापन पर ⋯ मेनू टैप करके Edit, Boost, Clone या Delete करें।",
            te: "Account → My Ads కి వెళ్లండి. ⋯ మెనూ నొక్కి Edit / Boost / Clone / Delete చేయండి.",
            ta: "Account → My Ads-க்குச் செல்லவும். ⋯ மெனுவை தட்டி Edit / Boost / Clone / Delete செய்யவும்.",
            es: "Ve a Account → My Ads. Toca el menú ⋯ para editar, impulsar, clonar o eliminar.",
        },
        action: { to: "/account/my-ads", label_i18n: { en: "My ads", hi: "मेरे विज्ञापन", te: "నా ప్రకటనలు", ta: "என் விளம்பரங்கள்", es: "Mis anuncios" } },
    },
    {
        id: "add-photos", category: "Post", question: "How many photos can I add?",
        triggers: ["photo", "photos", "pictures", "images", "how many photos", "upload image"],
        triggers_i18n: { hi: ["तस्वीर", "फोटो"], te: ["ఫోటో"], ta: ["படம்"], es: ["foto", "imagen"] },
        answer_i18n: {
            en: "You can add up to 8 photos per listing (JPG / PNG / HEIC, max 10 MB each). The first photo becomes your listing's cover. Clear daylight photos get 3x more clicks.",
            hi: "प्रति लिस्टिंग 8 तस्वीरें (JPG/PNG/HEIC, अधिकतम 10 MB प्रत्येक) जोड़ सकते हैं। पहली तस्वीर कवर बन जाती है।",
            te: "ప్రతి లిస్టింగ్‌కు 8 ఫోటోలు జోడించవచ్చు (JPG/PNG/HEIC, గరిష్టం 10 MB). మొదటి ఫోటో కవర్ అవుతుంది.",
            ta: "ஒரு பதிவுக்கு 8 படங்கள் (JPG/PNG/HEIC, அதிகபட்சம் 10 MB) சேர்க்கலாம். முதல் படம் கவர் ஆகும்.",
            es: "Puedes añadir hasta 8 fotos (JPG/PNG/HEIC, máx. 10 MB c/u). La primera foto es la portada.",
        },
    },

    // ---------- Digital Store ----------
    {
        id: "open-store", category: "Store", question: "How do I open a Digital Store?",
        triggers: ["open store", "digital store", "storefront", "shop page", "create store", "brand page"],
        triggers_i18n: { hi: ["डिजिटल स्टोर", "दुकान"], te: ["డిజిటల్ స్టోర్"], ta: ["டிஜிட்டல் ஸ்டோர்"], es: ["tienda digital", "abrir tienda"] },
        answer_i18n: {
            en: "Every Adukoo account gets a free Digital Store. Account → My Digital Store, add logo, cover, about + WhatsApp. Your URL becomes /digital-stores/{your-name}.",
            hi: "हर Adukoo अकाउंट को मुफ्त Digital Store मिलता है। Account → My Digital Store में logo, cover, about + WhatsApp जोड़ें।",
            te: "ప్రతి Adukoo ఖాతాకు ఉచిత Digital Store ఉంది. Account → My Digital Store → లోగో, కవర్, WhatsApp జోడించండి.",
            ta: "ஒவ்வொரு Adukoo கணக்கிற்கும் இலவச Digital Store உள்ளது. Account → My Digital Store → லோகோ, கவர், WhatsApp சேர்க்கவும்.",
            es: "Cada cuenta Adukoo tiene una Tienda Digital gratis. Account → My Digital Store → añade logo, portada y WhatsApp.",
        },
        action: { to: "/account", label_i18n: { en: "Open my store", hi: "मेरा स्टोर खोलें", te: "నా స్టోర్ తెరవండి", ta: "என் ஸ்டோர்", es: "Mi tienda" } },
    },
    {
        id: "verify-badge", category: "Store", question: "How do I get the Verified badge?",
        triggers: ["verified", "verified badge", "green tick", "how to verify", "trust badge"],
        triggers_i18n: { hi: ["वेरीफाइड", "हरा टिक"], te: ["వెరిఫైడ్"], ta: ["சரிபார்க்கப்பட்ட"], es: ["verificado", "verificar"] },
        answer_i18n: {
            en: "Account → Get Verified. Submit ID + phone OTP + real photo. Verified sellers get a green tick, priority in search + higher buyer trust.",
            hi: "Account → Get Verified। ID + फोन OTP + असली फोटो जमा करें। वेरीफाइड सेलर्स को हरा टिक + प्राथमिकता मिलती है।",
            te: "Account → Get Verified. ID + ఫోన్ OTP + ఫోటో సమర్పించండి. వెరిఫైడ్ సెల్లర్‌లకు గ్రీన్ టిక్ లభిస్తుంది.",
            ta: "Account → Get Verified. ID + phone OTP + photo சமர்ப்பிக்கவும். Verified sellers-க்கு green tick கிடைக்கும்.",
            es: "Account → Get Verified. Envía ID + OTP + foto. Los verificados reciben tick verde y prioridad.",
        },
        action: { to: "/account", label_i18n: { en: "Get verified", hi: "वेरीफाई करें", te: "వెరిఫై చేయండి", ta: "சரிபார்க்கவும்", es: "Verificar" } },
    },

    // ---------- Buying / browsing ----------
    {
        id: "browse-buy", category: "Buy", question: "How do I buy something on Adukoo?",
        triggers: ["buy", "browse", "find", "search", "purchase", "shopping"],
        triggers_i18n: { hi: ["खरीदना", "ब्राउज़"], te: ["కొనుగోలు"], ta: ["வாங்க"], es: ["comprar", "buscar"] },
        answer_i18n: {
            en: "Adukoo is a discovery platform — buyers chat directly with sellers via WhatsApp, in-app chat or in-person. Sellers keep 100% of every sale.",
            hi: "Adukoo एक डिस्कवरी प्लेटफ़ॉर्म है — खरीदार सीधे विक्रेता से WhatsApp / चैट / व्यक्तिगत रूप से बात करते हैं। विक्रेता 100% कमाई रखते हैं।",
            te: "Adukoo ఒక Discovery ప్లాట్‌ఫారం — కొనుగోలుదారులు నేరుగా విక్రేతలతో WhatsApp / చాట్ / వ్యక్తిగతంగా మాట్లాడతారు.",
            ta: "Adukoo ஒரு Discovery platform — வாங்குபவர்கள் நேரடியாக விற்பனையாளர்களுடன் WhatsApp / chat / நேரிலேயே பேசுகிறார்கள்.",
            es: "Adukoo es una plataforma de descubrimiento — el comprador habla directamente con el vendedor por WhatsApp / chat / en persona.",
        },
        action: { to: "/browse", label_i18n: { en: "Browse listings", hi: "ब्राउज़", te: "బ్రౌజ్", ta: "பார்க்க", es: "Explorar" } },
    },
    {
        id: "contact-seller", category: "Buy", question: "How do I contact a seller?",
        triggers: ["contact seller", "message seller", "reach seller", "call seller", "chat seller"],
        triggers_i18n: { hi: ["विक्रेता से संपर्क", "मैसेज"], te: ["విక్రేత"], ta: ["விற்பனையாளர்"], es: ["contactar vendedor"] },
        answer_i18n: {
            en: "Every listing has a Chat button (in-app chat) + a WhatsApp button (opens seller's WhatsApp directly). Adukoo takes zero cut of the conversation.",
            hi: "हर लिस्टिंग पर Chat + WhatsApp दोनों बटन हैं। Adukoo बातचीत से कोई कमीशन नहीं लेता।",
            te: "ప్రతి లిస్టింగ్‌లో Chat + WhatsApp బటన్‌లు ఉన్నాయి. Adukoo సంభాషణ నుండి కమిషన్ తీసుకోదు.",
            ta: "ஒவ்வொரு பதிவிலும் Chat + WhatsApp பொத்தான்கள் உள்ளன. Adukoo கமிஷன் எடுக்காது.",
            es: "Cada anuncio tiene Chat + WhatsApp. Adukoo no cobra comisión por la conversación.",
        },
    },

    // ---------- Jobs ----------
    {
        id: "post-job", category: "Jobs", question: "How do I post a job or hire?",
        triggers: ["post job", "hire", "recruit", "vacancy", "hiring", "open position"],
        triggers_i18n: { hi: ["नौकरी", "भर्ती"], te: ["ఉద్యోగం", "రిక్రూట్"], ta: ["வேலை", "பணி"], es: ["publicar empleo", "contratar"] },
        answer_i18n: {
            en: "Recruiters: + Post → pick Jobs → Title, JD, Salary, City. Candidates apply directly via chat.",
            hi: "भर्ती करने वाले: + Post → Jobs चुनें → शीर्षक, JD, वेतन, शहर भरें। उम्मीदवार चैट से आवेदन करते हैं।",
            te: "రిక్రూటర్లు: + Post → Jobs → శీర్షిక, JD, జీతం, నగరం. అభ్యర్థులు చాట్ ద్వారా దరఖాస్తు చేస్తారు.",
            ta: "பணியாளர்கள்: + Post → Jobs → தலைப்பு, JD, சம்பளம், நகரம். Candidates chat மூலம் விண்ணப்பிக்கிறார்கள்.",
            es: "Reclutadores: + Post → Jobs → título, descripción, salario, ciudad. Candidatos aplican por chat.",
        },
        action: { to: "/post-ad", label_i18n: { en: "Post a job", hi: "नौकरी पोस्ट करें", te: "ఉద్యోగం పోస్ట్", ta: "வேலை பதிவு", es: "Publicar empleo" } },
    },
    {
        id: "job-profile", category: "Jobs", question: "How do I post my resume / apply for a job?",
        triggers: ["apply job", "post resume", "job profile", "job seeker", "looking for job", "cv"],
        triggers_i18n: { hi: ["नौकरी की तलाश", "बायोडाटा", "रिज्यूमे"], te: ["ఉద్యోగ దరఖాస్తు", "రిజ్యూమ్"], ta: ["வேலைக்கு விண்ணப்பம்", "ரெசுமே"], es: ["buscar empleo", "curriculum"] },
        answer_i18n: {
            en: "Candidates: + Post → pick Job Profiles → your resume becomes a public profile. Recruiters chat with you directly.",
            hi: "उम्मीदवार: + Post → Job Profiles चुनें → आपका रिज्यूमे सार्वजनिक हो जाता है। भर्तीकर्ता सीधे चैट करते हैं।",
            te: "అభ్యర్థులు: + Post → Job Profiles → మీ రిజ్యూమ్ పబ్లిక్ ప్రొఫైల్ అవుతుంది.",
            ta: "Candidates: + Post → Job Profiles → உங்கள் resume public profile ஆகும்.",
            es: "Candidatos: + Post → Job Profiles → tu currículum se vuelve público. Los reclutadores te contactan.",
        },
        action: { to: "/post-ad", label_i18n: { en: "Post my resume", hi: "रिज्यूमे पोस्ट", te: "రిజ్యూమ్ పోస్ట్", ta: "ரெசுமே பதிவு", es: "Publicar CV" } },
    },

    // ---------- Affiliate / Sponsored ----------
    {
        id: "affiliate", category: "Earn", question: "How do I post an affiliate deal?",
        triggers: ["affiliate", "sponsored", "commission", "amazon deal", "flipkart deal", "earn money", "earnkaro", "admitad"],
        triggers_i18n: { hi: ["एफिलिएट", "कमीशन", "कमाई"], te: ["అఫిలియేట్"], ta: ["அஃபிலியேட்"], es: ["afiliado", "comisión"] },
        answer_i18n: {
            en: "+ Post → Sponsored / Affiliate deal → paste your partner URL (Amazon, Flipkart, EarnKaro, Admitad, MakeMyTrip). Auto-labelled SPONSORED. You keep 100% commission.",
            hi: "+ Post → Sponsored / Affiliate → अपना partner URL पेस्ट करें। SPONSORED ribbon स्वचालित। 100% कमीशन आपका।",
            te: "+ Post → Sponsored / Affiliate → మీ partner URL పేస్ట్ చేయండి. SPONSORED ribbon ఆటోమాటిక్. 100% కమిషన్ మీది.",
            ta: "+ Post → Sponsored / Affiliate → உங்கள் partner URL paste. SPONSORED ribbon auto. 100% commission உங்களுடையது.",
            es: "+ Post → Sponsored / Affiliate → pega tu URL (Amazon, Flipkart, etc.). SPONSORED automático. 100% comisión.",
        },
        action: { to: "/post-ad", label_i18n: { en: "Post an affiliate deal", hi: "एफिलिएट पोस्ट", te: "అఫిలియేట్ పోస్ట్", ta: "அஃபிலியேட் பதிவு", es: "Publicar afiliado" } },
    },

    // ---------- Funboard ----------
    {
        id: "funboard-what", category: "Funboard", question: "What is Funboard?",
        triggers: ["funboard", "fun board", "swipe feed", "memes", "news feed"],
        triggers_i18n: { hi: ["फनबोर्ड", "मीम्स"], te: ["ఫన్‌బోర్డ్"], ta: ["ஃபன்போர்டு"], es: ["funboard", "memes"] },
        answer_i18n: {
            en: "Funboard is Adukoo's live feed of news (20+ countries), memes, videos, quotes and marketplace picks — auto-refreshed every 2 minutes. On any card you can Like ❤️, Save 🔖, Share 🔗 and Follow 🔔 the publisher. Paste any public link (YouTube / IG / X / news) to share it to Funboard yourself.",
            hi: "Funboard एक Live फ़ीड है — 20+ देशों की news, memes, videos, quotes + marketplace picks (हर 2 मिनट में auto-refresh). हर card पर Like / Save / Share / Follow है। कोई भी public link paste करके आप खुद Funboard पर share कर सकते हैं।",
            te: "Funboard అనేది Live feed — 20+ దేశాల news, memes, videos, quotes + marketplace picks (ప్రతి 2 నిమిషాలకు auto-refresh). ప్రతి కార్డ్‌లో Like / Save / Share / Follow ఉన్నాయి. లింక్ paste చేసి మీరూ share చేయవచ్చు.",
            ta: "Funboard-ல் 20+ நாடுகளின் news, memes, videos, quotes + marketplace picks (2 நிமிடத்திற்கு ஒரு முறை auto-refresh). ஒவ்வொரு card-லும் Like / Save / Share / Follow. Link paste செய்து நீங்களே share செய்யலாம்.",
            es: "Funboard es el feed en vivo de Adukoo — noticias de 20+ países, memes, videos, frases y ofertas del marketplace, actualizado cada 2 minutos. En cada carta: Like / Save / Share / Follow. Pega cualquier enlace público para compartirlo tú también.",
        },
        action: { to: "/funboard", label_i18n: { en: "Open Funboard", hi: "Funboard खोलें", te: "Funboard తెరవండి", ta: "Funboard திற", es: "Abrir Funboard" } },
    },
    {
        id: "funboard-share-link", category: "Funboard", question: "How do I share a link to Funboard?",
        triggers: ["share to funboard", "post to funboard", "paste link", "share youtube", "share instagram", "share reel"],
        triggers_i18n: { hi: ["लिंक शेयर", "फनबोर्ड पर पोस्ट"], te: ["లింక్ share"], ta: ["link share"], es: ["compartir enlace", "publicar en funboard"] },
        answer_i18n: {
            en: "Two ways: 1) On the Home page tap the yellow 🎉 floating button and paste your link. 2) Inside Funboard, tap the 'Paste a link…' bar at the top of the feed. YouTube / Instagram / X / news / blog links all work — we fetch the preview automatically.",
            hi: "दो तरीके: 1) Home पर पीले 🎉 button पर tap करें और link paste करें। 2) Funboard के अंदर feed के ऊपर 'Paste a link…' पर tap करें। YouTube / IG / X / news links — सब चलते हैं।",
            te: "రెండు మార్గాలు: 1) Home పేజీలో పసుపు 🎉 button. 2) Funboard-లో 'Paste a link…' bar. YouTube / IG / X / news links — అన్నీ పనిచేస్తాయి.",
            ta: "இரண்டு வழிகள்: 1) Home-ல் 🎉 button. 2) Funboard-ல் 'Paste a link…' bar. YouTube / IG / X / news links எல்லாம் வேலை செய்யும்.",
            es: "Dos formas: 1) Botón amarillo 🎉 en Home. 2) Barra 'Paste a link…' arriba en Funboard. Funciona con YouTube / IG / X / noticias / blogs — la vista previa se obtiene sola.",
        },
        action: { to: "/funboard", label_i18n: { en: "Try it now", hi: "अभी शेयर करें", te: "ఇప్పుడు share చేయండి", ta: "இப்போது share செய்", es: "Probar ahora" } },
    },
    {
        id: "funboard-following", category: "Funboard", question: "What is the Following tab?",
        triggers: ["following tab", "follow publisher", "my sources", "follow news"],
        triggers_i18n: { hi: ["फॉलो"], te: ["ఫాలో"], ta: ["follow"], es: ["seguir", "siguiendo"] },
        answer_i18n: {
            en: "Tap 🔔 Follow on any card to add that publisher to your Following list. Then tap the 'Following' tab at the top of Funboard to see a chronological feed of only those sources — no algorithm, no ads mixing in, just your picks.",
            hi: "किसी भी card पर 🔔 Follow tap करें → publisher आपकी list में add। फिर Funboard के ऊपर 'Following' tab पर tap करें — सिर्फ आपके picks, algorithm नहीं।",
            te: "ఏదైనా కార్డ్‌లో 🔔 Follow tap చేయండి → publisher మీ list-లో add. తర్వాత 'Following' tab-ని tap చేయండి — algorithm లేకుండా మీ picks మాత్రమే.",
            ta: "எந்த card-லும் 🔔 Follow tap செய்யவும் → publisher உங்கள் list-ல் add. அதன் பிறகு 'Following' tab-ல் — algorithm இல்லை, உங்கள் picks மட்டும்.",
            es: "Toca 🔔 Follow en cualquier carta para añadir ese publisher. Luego abre la pestaña 'Following' arriba en Funboard — solo tus fuentes, cronológicamente, sin algoritmo.",
        },
        action: { to: "/funboard", label_i18n: { en: "Open Funboard", hi: "Funboard खोलें", te: "Funboard తెరవండి", ta: "Funboard திற", es: "Abrir Funboard" } },
    },
    {
        id: "funboard-filters", category: "Funboard", question: "How do Region and Category filters work?",
        triggers: ["region", "country funboard", "category funboard", "filter funboard"],
        triggers_i18n: { hi: ["क्षेत्र", "श्रेणी"], te: ["ప్రాంతం", "వర్గం"], ta: ["region", "category"], es: ["región", "categoría"] },
        answer_i18n: {
            en: "Tap the ⚙️ Filter icon in the Funboard header to pick a region (auto-detect / IN / US / GB / and 15+ more). Category chips run right below the header — tap one (e.g. Sports, Memes, Tech) to focus the feed. Combine both to get, say, Indian Politics or US Sports.",
            hi: "Funboard header में ⚙️ Filter icon → region चुनें (auto / IN / US / GB और 15+). Category chips नीचे — Sports, Memes, Tech आदि। दोनों combine करके 'India Politics' या 'US Sports' पाएं।",
            te: "Funboard header-లో ⚙️ Filter icon → region ఎంచుకోండి. Category chips క్రింద — Sports, Memes, Tech.",
            ta: "Funboard header-ல் ⚙️ Filter → region choose செய்யவும். கீழே category chips.",
            es: "Icono ⚙️ Filtro en el encabezado → elige región. Debajo, chips de categoría (Sports, Memes, Tech…). Combínalos para lograr 'India Política' o 'US Deportes'.",
        },
        action: { to: "/funboard", label_i18n: { en: "Open Funboard", hi: "Funboard खोलें", te: "Funboard తెరవండి", ta: "Funboard திற", es: "Abrir Funboard" } },
    },
    {
        id: "funboard-saved", category: "Funboard", question: "Where do I see my saved Funboard cards?",
        triggers: ["saved funboard", "my funboard", "liked memes", "shared cards"],
        triggers_i18n: { hi: ["सेव्ड फनबोर्ड"], te: ["సేవ్ ఫన్‌బోర్డ్"], ta: ["சேமித்த"], es: ["guardados funboard"] },
        answer_i18n: {
            en: "Every card has Like ❤️, Save 🔖, Share 🔗 and Follow 🔔. Your bookmarks live at My Funboard with 3 tabs: Saved, Liked, Shared.",
            hi: "हर card पर Like / Save / Share / Follow है। सब कुछ My Funboard में — 3 tabs: Saved, Liked, Shared.",
            te: "ప్రతి కార్డ్‌లో Like / Save / Share / Follow. అన్నీ My Funboard-లో — 3 tabs.",
            ta: "ஒவ்வொரு card-லும் Like / Save / Share / Follow. My Funboard-ல் 3 tabs.",
            es: "Cada carta tiene Like / Save / Share / Follow. Todo en My Funboard — 3 pestañas.",
        },
        action: { to: "/funboard/saved", label_i18n: { en: "My Funboard", hi: "My Funboard", te: "My Funboard", ta: "My Funboard", es: "Mi Funboard" } },
    },
    {
        id: "funboard-delete", category: "Funboard", question: "How do I delete a Funboard post I shared?",
        triggers: ["delete funboard", "remove funboard", "delete my share"],
        triggers_i18n: { hi: ["डिलीट फनबोर्ड"], te: ["డిలీట్"], ta: ["delete"], es: ["borrar funboard"] },
        answer_i18n: {
            en: "Open the card you shared — the 🗑️ Delete chip appears in the action row. Only you (or an Adukoo admin, for moderation) can delete a card. Deletion is instant and irreversible.",
            hi: "अपने shared card पर 🗑️ Delete chip दिखेगा। सिर्फ आप या Adukoo admin (moderation के लिए) card delete कर सकते हैं।",
            te: "మీరు share చేసిన కార్డ్‌లో 🗑️ Delete chip కనిపిస్తుంది. మీరు లేదా Adukoo admin మాత్రమే delete చేయగలరు.",
            ta: "நீங்கள் share செய்த card-ல் 🗑️ Delete chip தெரியும். நீங்கள் அல்லது Adukoo admin மட்டுமே delete செய்ய முடியும்.",
            es: "En tu carta compartida verás el chip 🗑️ Delete. Solo tú o un admin de Adukoo pueden borrarla.",
        },
    },
    {
        id: "funboard-report", category: "Funboard", question: "How do I report a bad Funboard card?",
        triggers: ["report funboard", "report card", "bad content", "adult content", "flag"],
        triggers_i18n: { hi: ["रिपोर्ट"], te: ["రిపోర్ట్"], ta: ["புகார்"], es: ["reportar", "denunciar"] },
        answer_i18n: {
            en: "Every card has a Report (flag) icon. Tap → pick reason → submit. 3 reports auto-hide the card. Reporters stay anonymous.",
            hi: "हर card पर Report (flag) icon है। Tap → कारण चुनें → submit। 3 reports पर card auto-hide होता है।",
            te: "ప్రతి కార్డ్‌లో Report (flag) icon ఉంది. 3 reports అయితే auto-hide.",
            ta: "ஒவ்வொரு card-லும் Report (flag) icon உள்ளது. 3 reports வந்தால் auto-hide.",
            es: "Cada carta tiene un icono Report. Con 3 reportes se oculta automáticamente.",
        },
    },

    // ---------- Account & payments ----------
    {
        id: "reset-password", category: "Account", question: "I forgot my password — how do I reset it?",
        triggers: ["forgot password", "reset password", "cant login", "cannot login", "recover", "lost password"],
        triggers_i18n: { hi: ["पासवर्ड भूल गया"], te: ["పాస్‌వర్డ్"], ta: ["கடவுச்சொல்"], es: ["contraseña olvidada", "recuperar"] },
        answer_i18n: {
            en: "Sign In → Forgot password. Enter email → we send a reset link in 30 seconds. Check spam if it doesn't arrive.",
            hi: "Sign In → Forgot password। ईमेल दर्ज करें → 30 सेकंड में reset link आएगा। Spam फ़ोल्डर चेक करें।",
            te: "Sign In → Forgot password. ఇమెయిల్ ఇవ్వండి → 30 సెకన్లలో reset link వస్తుంది.",
            ta: "Sign In → Forgot password. Email input → 30 seconds-ல் reset link வரும்.",
            es: "Sign In → Forgot password. Ingresa tu email → recibirás el enlace en 30 segundos.",
        },
        action: { to: "/auth", label_i18n: { en: "Sign in", hi: "साइन इन", te: "సైన్ ఇన్", ta: "உள்நுழை", es: "Iniciar sesión" } },
    },
    {
        id: "change-language", category: "Account", question: "How do I change the app language?",
        triggers: ["language", "change language", "switch language", "translate"],
        triggers_i18n: { hi: ["भाषा"], te: ["భాష"], ta: ["மொழி"], es: ["cambiar idioma"] },
        answer_i18n: {
            en: "Adukoo works in 65+ languages. Tap the globe icon in the header → pick your language. Setting is saved to your profile.",
            hi: "Adukoo 65+ भाषाओं में उपलब्ध है। हेडर में globe icon पर टैप करें → अपनी भाषा चुनें।",
            te: "Adukoo 65+ భాషల్లో అందుబాటులో ఉంది. Globe icon → మీ భాష ఎంచుకోండి.",
            ta: "Adukoo 65+ மொழிகளில் உள்ளது. Globe icon → உங்கள் மொழியை தேர்ந்தெடுக்கவும்.",
            es: "Adukoo funciona en 65+ idiomas. Toca el icono globo → elige tu idioma.",
        },
    },
    {
        id: "delete-account", category: "Account", question: "How do I delete my Adukoo account?",
        triggers: ["delete account", "close account", "deactivate", "remove account"],
        triggers_i18n: { hi: ["खाता हटाना"], te: ["ఖాతా తొలగించు"], ta: ["கணக்கு நீக்கு"], es: ["eliminar cuenta"] },
        answer_i18n: {
            en: "Account → Settings → Delete account. Permanent — listings/chats/reviews removed within 24h. If you just want a break, sign out.",
            hi: "Account → Settings → Delete account। स्थायी — listings/chats/reviews 24 घंटे में हट जाएंगे।",
            te: "Account → Settings → Delete account. శాశ్వతం — 24 గంటలలో అన్నీ తీసివేయబడతాయి.",
            ta: "Account → Settings → Delete account. நிரந்தரம் — 24 hours-ல் listings/chats/reviews நீக்கப்படும்.",
            es: "Account → Settings → Delete account. Permanente — se elimina en 24 h.",
        },
    },
    {
        id: "notifications", category: "Account", question: "How do I manage notifications?",
        triggers: ["notifications", "notification settings", "turn off notifications", "email alerts"],
        triggers_i18n: { hi: ["नोटिफिकेशन"], te: ["నోటిఫికేషన్"], ta: ["அறிவிப்பு"], es: ["notificaciones"] },
        answer_i18n: {
            en: "Account → Notifications. Toggle email, push, or in-app for messages, listing updates, boost offers etc.",
            hi: "Account → Notifications। Email, push, in-app अलग-अलग toggle कर सकते हैं।",
            te: "Account → Notifications. Email / push / in-app toggle చేయవచ్చు.",
            ta: "Account → Notifications. Email / push / in-app toggle செய்யலாம்.",
            es: "Account → Notifications. Alternar email, push o in-app.",
        },
    },

    // ---------- Trust & safety ----------
    {
        id: "scam-report", category: "Trust", question: "How do I report a scam / fake listing?",
        triggers: ["scam", "fraud", "fake", "report user", "report listing", "spam", "cheat"],
        triggers_i18n: { hi: ["धोखा", "फर्जी"], te: ["మోసం"], ta: ["ஏமாற்று"], es: ["estafa", "falso"] },
        answer_i18n: {
            en: "Open the listing → Report → 'Scam / Fake' → submit. We review within 24h. If money changed hands, email reachus@adukoo.com + file police complaint.",
            hi: "Listing खोलें → Report → 'Scam / Fake' → submit। 24 घंटे में समीक्षा। पैसे गए हैं तो reachus@adukoo.com + पुलिस।",
            te: "లిస్టింగ్ తెరిచి → Report → 'Scam/Fake' → submit. 24 గంటల్లో సమీక్ష.",
            ta: "Listing திற → Report → 'Scam / Fake' → submit. 24 hours-ல் மறுஆய்வு.",
            es: "Abre el anuncio → Report → 'Scam / Fake' → enviar. Revisamos en 24 h.",
        },
    },
    {
        id: "commission-fees", category: "Trust", question: "Do you charge a commission on my sale?",
        triggers: ["commission", "fee", "cut", "charge", "percentage", "how much cost", "pricing", "free"],
        triggers_i18n: { hi: ["कमीशन", "शुल्क", "मुफ्त"], te: ["కమిషన్", "ఉచితం"], ta: ["கமிஷன்", "இலவசம்"], es: ["comisión", "gratis"] },
        answer_i18n: {
            en: "No. Adukoo takes 0% of your sale. Basic listings are free forever. We earn only from Sponsored slots + optional Verified-Business subscriptions.",
            hi: "नहीं। Adukoo 0% कमीशन लेता है। बेसिक listings हमेशा मुफ़्त। हम Sponsored slots + Verified-Business subscription से कमाते हैं।",
            te: "లేదు. Adukoo 0% కమిషన్ తీసుకుంటుంది. బేసిక్ listings ఎప్పటికీ ఉచితం.",
            ta: "இல்லை. Adukoo 0% commission எடுக்கும். Basic listings எப்போதும் இலவசம்.",
            es: "No. Adukoo cobra 0% de tu venta. Los anuncios básicos son gratis para siempre.",
        },
    },
    {
        id: "meeting-safety", category: "Trust", question: "Is it safe to meet a buyer / seller?",
        triggers: ["meet", "meeting", "in person", "safety", "safe to meet"],
        triggers_i18n: { hi: ["सुरक्षित मिलना"], te: ["సురక్షితం"], ta: ["பாதுகாப்பு"], es: ["reunión segura"] },
        answer_i18n: {
            en: "Always meet in a public place, in daylight, with a friend if possible. Never share OTPs. Verify the item before paying. Prefer UPI / cash / bank transfer over crypto or gift cards.",
            hi: "हमेशा सार्वजनिक स्थान पर, दिन के उजाले में मिलें, हो सके तो दोस्त के साथ। OTP कभी शेयर न करें। भुगतान से पहले सामान जांचें।",
            te: "ఎల్లప్పుడూ పబ్లిక్ ప్రదేశంలో, పగలు, స్నేహితుడితో కలవండి. OTP ఎప్పుడూ షేర్ చేయవద్దు.",
            ta: "எப்போதும் public இடத்தில், பகல் நேரத்தில், நண்பருடன் சந்திக்கவும். OTP share செய்ய வேண்டாம்.",
            es: "Reúnete en un lugar público, de día, con un amigo si es posible. Nunca compartas OTPs.",
        },
    },

    // ---------- Categories ----------
    {
        id: "categories", category: "Browse", question: "What categories do you have?",
        triggers: ["category", "categories", "sections", "types of ads", "what can i sell"],
        triggers_i18n: { hi: ["श्रेणी"], te: ["వర్గం"], ta: ["வகை"], es: ["categoría"] },
        answer_i18n: {
            en: "Mobiles, Electronics, Vehicles, Real Estate, Fashion, Jobs, Job Profiles, Services, Home & Garden, Matrimony, Pets, Sports, Kids, Books, Sponsored/Affiliate, and more — 25+ categories worldwide.",
            hi: "मोबाइल, इलेक्ट्रॉनिक्स, वाहन, रियल एस्टेट, फ़ैशन, नौकरी, सेवाएं, घर, विवाह, पालतू जानवर, खेल, बच्चे, किताबें — 25+ श्रेणियां।",
            te: "మొబైల్స్, ఎలక్ట్రానిక్స్, వాహనాలు, రియల్ ఎస్టేట్, ఫ్యాషన్, ఉద్యోగాలు, సేవలు, హోమ్, వివాహం — 25+ వర్గాలు.",
            ta: "Mobiles, Electronics, Vehicles, Real Estate, Fashion, Jobs, Services, Home, Matrimony — 25+ categories.",
            es: "Móviles, Electrónica, Vehículos, Bienes Raíces, Moda, Empleos, Servicios, Hogar, Matrimonio — 25+ categorías.",
        },
        action: { to: "/browse", label_i18n: { en: "Browse categories", hi: "श्रेणियां देखें", te: "వర్గాలు చూడండి", ta: "வகைகள்", es: "Explorar" } },
    },

    // ---------- Help / support ----------
    {
        id: "human-support", category: "Support", question: "I need to talk to a human",
        triggers: ["human", "support", "help me", "contact adukoo", "email support", "call support", "not working", "bug"],
        triggers_i18n: { hi: ["मानव", "मदद", "बग"], te: ["సహాయం", "మనిషి"], ta: ["மனிதன்", "உதவி"], es: ["humano", "soporte"] },
        answer_i18n: {
            en: "Email reachus@adukoo.com — we reply within 24h (usually faster). Include a screenshot + your account email.",
            hi: "reachus@adukoo.com पर ईमेल करें — हम 24 घंटे में जवाब देते हैं। Screenshot + account email शामिल करें।",
            te: "reachus@adukoo.com కు ఇమెయిల్ చేయండి — 24 గంటల్లో ప్రతిస్పందిస్తాం.",
            ta: "reachus@adukoo.com-க்கு email செய்யவும் — 24 hours-ல் பதிலளிக்கிறோம்.",
            es: "Escribe a reachus@adukoo.com — respondemos en 24 h.",
        },
        action: { to: "mailto:reachus@adukoo.com", label_i18n: { en: "Email support", hi: "ईमेल सपोर्ट", te: "ఇమెయిల్", ta: "Email", es: "Email soporte" } },
    },
    {
        id: "guide", category: "Support", question: "Where is the full guide?",
        triggers: ["guide", "documentation", "docs", "tutorial", "manual", "how it works"],
        triggers_i18n: { hi: ["गाइड", "ट्यूटोरियल"], te: ["గైడ్"], ta: ["வழிகாட்டி"], es: ["guía", "tutorial"] },
        answer_i18n: {
            en: "Full guide at /guide. FAQ at /faq. Digital Store playbook at /how-it-works/digital-stores.",
            hi: "पूरी गाइड /guide पर। FAQ /faq पर। Digital Store playbook /how-it-works/digital-stores पर।",
            te: "పూర్తి గైడ్ /guide లో. FAQ /faq లో.",
            ta: "முழு வழிகாட்டி /guide. FAQ /faq.",
            es: "Guía completa en /guide. FAQ en /faq.",
        },
        action: { to: "/guide", label_i18n: { en: "Open the Guide", hi: "गाइड खोलें", te: "గైడ్ తెరవండి", ta: "வழிகாட்டி", es: "Abrir Guía" } },
    },
    {
        id: "investors", category: "Support", question: "Are you looking for investors / partners?",
        triggers: ["investor", "invest", "partnership", "funding", "raise money", "vc"],
        triggers_i18n: { hi: ["निवेशक"], te: ["పెట్టుబడిదారు"], ta: ["முதலீட்டாளர்"], es: ["inversores"] },
        answer_i18n: {
            en: "Yes — actively talking to strategic investors and partners. Public pitch at /investors, then email reachus@adukoo.com.",
            hi: "हां — हम रणनीतिक निवेशकों/भागीदारों से बात कर रहे हैं। Public pitch /investors पर।",
            te: "అవును — వ్యూహాత్మక పెట్టుబడిదారులతో మాట్లాడుతున్నాం. /investors చూడండి.",
            ta: "ஆம் — முதலீட்டாளர்களுடன் பேசி வருகிறோம். /investors பார்க்கவும்.",
            es: "Sí — hablando activamente con inversores. Pitch en /investors.",
        },
        action: { to: "/investors", label_i18n: { en: "See Investor page", hi: "निवेशक पेज", te: "పెట్టుబడి పేజీ", ta: "முதலீட்டு பக்கம்", es: "Ver Investor" } },
    },
    {
        id: "for-businesses", category: "Support", question: "I run a business — how do I use Adukoo for my brand?",
        triggers: ["business", "brand", "company", "enterprise", "for businesses", "mnc", "corporate"],
        triggers_i18n: { hi: ["व्यवसाय", "कंपनी"], te: ["వ్యాపారం"], ta: ["வணிகம்"], es: ["empresa", "marca"] },
        answer_i18n: {
            en: "See /for-businesses. Contact reachus@adukoo.com for a custom package — Starter ₹15K → Corporate ₹10L per year. Done-for-you setup: Digital Store, campaigns, sponsored slots, analytics.",
            hi: "देखें /for-businesses। कस्टम पैकेज के लिए reachus@adukoo.com — Starter ₹15K से Corporate ₹10L/वर्ष।",
            te: "/for-businesses చూడండి. reachus@adukoo.com — Starter ₹15K నుండి Corporate ₹10L/year.",
            ta: "/for-businesses பார்க்கவும். reachus@adukoo.com — Starter ₹15K முதல் Corporate ₹10L/year.",
            es: "Ve /for-businesses. Contáctanos en reachus@adukoo.com — Starter ₹15K a Corporate ₹10L/año.",
        },
        action: { to: "/for-businesses", label_i18n: { en: "For Businesses", hi: "व्यवसायों के लिए", te: "వ్యాపారాలకు", ta: "வணிகங்களுக்கு", es: "Para Empresas" } },
    },
    {
        id: "privacy", category: "Trust", question: "Is my data safe on Adukoo?",
        triggers: ["privacy", "data safe", "personal data", "gdpr", "share data", "data protection"],
        triggers_i18n: { hi: ["गोपनीयता", "डेटा"], te: ["గోప్యత"], ta: ["தனியுரிமை"], es: ["privacidad"] },
        answer_i18n: {
            en: "We never sell your data to third parties. Only info you make public (listing, Digital Store, reviews) is visible. Full policy at /privacy.",
            hi: "हम आपका डेटा किसी को नहीं बेचते। सिर्फ़ आपके publish किए गए listings/Store/reviews दिखते हैं।",
            te: "మీ డేటాను ఎవరికీ అమ్మము. మీరు పబ్లిష్ చేసిన listings/Store/reviews మాత్రమే కనిపిస్తాయి.",
            ta: "உங்கள் data-வை யாருக்கும் விற்கமாட்டோம். Public listings/Store/reviews மட்டும் தெரியும்.",
            es: "Nunca vendemos tus datos. Solo lo que publiques (anuncios, tienda, reseñas) es visible.",
        },
        action: { to: "/privacy", label_i18n: { en: "Privacy policy", hi: "गोपनीयता नीति", te: "గోప్యతా విధానం", ta: "தனியுரிமை", es: "Privacidad" } },
    },
];

// ---------- Public API ----------

const SUPPORTED_LANGS = ["en", "hi", "te", "ta", "es"];

export function pickLang(user) {
    const cand = (user?.language || "").toLowerCase();
    if (SUPPORTED_LANGS.includes(cand)) return cand;
    const browser = ((typeof navigator !== "undefined" ? navigator.language : "") || "en").slice(0, 2).toLowerCase();
    if (SUPPORTED_LANGS.includes(browser)) return browser;
    return "en";
}

/** Localise an intent for the given language, falling back to English. */
function localise(intent, lang) {
    const l = SUPPORTED_LANGS.includes(lang) ? lang : "en";
    return {
        id: intent.id,
        category: intent.category,
        question: intent.question,
        answer: intent.answer_i18n?.[l] || intent.answer_i18n?.en,
        action: intent.action ? {
            to: intent.action.to,
            label: intent.action.label_i18n?.[l] || intent.action.label_i18n?.en,
        } : null,
    };
}

/** Localise all intents at once. */
export function getLocalisedIntents(lang) {
    return RAW_INTENTS.map((i) => localise(i, lang));
}

/** The first-open quick-reply chips, localised. */
export function getLocalisedQuickReplies(lang) {
    const wanted = ["post-ad", "open-store", "verify-badge", "affiliate", "post-job", "funboard-what", "reset-password", "commission-fees", "human-support"];
    return wanted.map((id) => {
        const raw = RAW_INTENTS.find((r) => r.id === id);
        return raw ? { intent: id, label: localise(raw, lang).question } : null;
    }).filter(Boolean);
}

/**
 * Fuzzy matcher — checks EN triggers + localised triggers for the given
 * language. Case-insensitive substring match, weighted by trigger length.
 */
export function matchIntent(userText, lang = "en") {
    if (!userText || !userText.trim()) return null;
    const q = userText.toLowerCase().trim();
    let best = null;
    let bestScore = 0;
    for (const intent of RAW_INTENTS) {
        let score = 0;
        for (const t of intent.triggers || []) {
            if (q.includes(t.toLowerCase())) score += t.length;
        }
        const localTriggers = intent.triggers_i18n?.[lang] || [];
        for (const t of localTriggers) {
            if (q.includes(t.toLowerCase())) score += t.length + 2; // slight boost
        }
        const questionWords = intent.question.toLowerCase().split(/\W+/).filter(w => w.length > 3);
        for (const w of questionWords) {
            if (q.includes(w)) score += 1;
        }
        if (score > bestScore) {
            bestScore = score;
            best = intent;
        }
    }
    return bestScore >= 4 ? (best ? localise(best, lang) : null) : null;
}

export function getIntentById(id, lang = "en") {
    const raw = RAW_INTENTS.find((i) => i.id === id);
    return raw ? localise(raw, lang) : null;
}

// Language-specific UI strings shown by the widget itself.
export const HELPER_UI = {
    en: {
        greeting: "Hi 👋 I'm the Adukoo Helper. Ask me how to do anything on Adukoo — post an ad, open a Digital Store, get verified, use Funboard, or anything else. Try a quick topic below or just type your question.",
        placeholder: "Ask anything about Adukoo…",
        needHelp: "Need help?",
        needHelpDesc: "Ask the Adukoo Helper how to post an ad, open a store, or get verified.",
        title: "Adukoo Helper",
        subtitle: "Free · Instant answers",
        reset: "Reset",
        fallback: "I didn't quite catch that. Here are some topics I can help with — tap one, or rephrase your question. For anything I can't handle, email reachus@adukoo.com.",
    },
    hi: {
        greeting: "नमस्ते 👋 मैं Adukoo Helper हूँ। पूछें — विज्ञापन कैसे पोस्ट करें, Digital Store कैसे खोलें, verified कैसे हों, Funboard कैसे इस्तेमाल करें, या कुछ भी।",
        placeholder: "Adukoo के बारे में कुछ भी पूछें…",
        needHelp: "मदद चाहिए?",
        needHelpDesc: "Adukoo Helper से पूछें कि विज्ञापन कैसे पोस्ट करें, स्टोर कैसे खोलें।",
        title: "Adukoo सहायक",
        subtitle: "मुफ़्त · तुरंत जवाब",
        reset: "रीसेट",
        fallback: "मैं समझ नहीं पाया। नीचे कोई विषय चुनें या दोबारा लिखें। जो मैं नहीं कर सकता उसके लिए reachus@adukoo.com पर ईमेल करें।",
    },
    te: {
        greeting: "నమస్తే 👋 నేను Adukoo Helper. ప్రకటన పోస్ట్ చేయడం, Digital Store తెరవడం, verified పొందడం — ఏదైనా అడగండి.",
        placeholder: "Adukoo గురించి ఏదైనా అడగండి…",
        needHelp: "సహాయం కావాలా?",
        needHelpDesc: "Adukoo Helper ను అడగండి.",
        title: "Adukoo సహాయకుడు",
        subtitle: "ఉచితం · తక్షణ సమాధానం",
        reset: "రీసెట్",
        fallback: "అర్థం కాలేదు. క్రింది టాపిక్ ఎంచుకోండి లేదా మళ్లీ టైప్ చేయండి. reachus@adukoo.com కు ఇమెయిల్ చేయవచ్చు.",
    },
    ta: {
        greeting: "வணக்கம் 👋 நான் Adukoo Helper. விளம்பரம் பதிவிடுவது, Digital Store திறப்பது — எதுவாக இருந்தாலும் கேளுங்கள்.",
        placeholder: "Adukoo பற்றி எதுவாக இருந்தாலும் கேளுங்கள்…",
        needHelp: "உதவி வேண்டுமா?",
        needHelpDesc: "Adukoo Helper-ஐ கேளுங்கள்.",
        title: "Adukoo உதவியாளர்",
        subtitle: "இலவசம் · உடனடி பதில்",
        reset: "மீட்டமை",
        fallback: "புரியவில்லை. கீழே topic தேர்ந்தெடுக்கவும் அல்லது மீண்டும் type செய்யவும். reachus@adukoo.com email செய்யலாம்.",
    },
    es: {
        greeting: "Hola 👋 Soy el Adukoo Helper. Pregúntame cómo publicar un anuncio, abrir una Tienda Digital, verificarte, usar Funboard o cualquier otra cosa.",
        placeholder: "Pregunta lo que quieras sobre Adukoo…",
        needHelp: "¿Necesitas ayuda?",
        needHelpDesc: "Pregúntale al Adukoo Helper.",
        title: "Adukoo Ayuda",
        subtitle: "Gratis · Respuestas al instante",
        reset: "Reiniciar",
        fallback: "No entendí. Elige un tema abajo o reformula. Para lo que no pueda ayudar, escribe a reachus@adukoo.com.",
    },
};
